import { PartyType, RelationshipTypeCode } from './contacts';

/** The configuration page's setting sections, in page order. */
export type SettingCategory =
    | 'CONTACTS'
    | 'CASES'
    | 'TIMECARDS'
    | 'INVOICE_CREATION'
    | 'INVOICE_REVIEW'
    | 'INVOICE_DATES'
    | 'COLLEAGUES';

/** `TENANT` is the whole firm; `EMPLOYEE` the colleagues of the allow-list only. */
export type SettingScope = 'TENANT' | 'EMPLOYEE';

/** A privilege role management lists only while the setting is on (`SHOWS`) or only while it is off (`HIDES`). */
export interface GovernedPrivilege {
    code: string;
    direction: 'SHOWS' | 'HIDES';
}

/** One back-office setting of the firm, as `GET /v1/back-office-settings` lists it. */
export interface SettingListElement {
    key: string;
    category: SettingCategory;
    /** The feature flag of the paid module the setting lives inside, or null. */
    paidFlag: string | null;
    governedPrivileges: GovernedPrivilege[];
    firmOnlyWarning: boolean;
    enabled: boolean;
    /** Kept while the setting is off: switching it on again restores it. */
    scope: SettingScope;
    /** The stored allow-list, kept while the scope is `TENANT`. */
    allowedEmployees: { id: number; fullName: string }[];
    /** The sub-options in declaration order. */
    options: { key: string; ticked: boolean }[];
}

/** The keys of the declared settings, as the backend declares them. */
export enum Setting {
    PERSON_TITLES_FREE_TEXT = 'PERSON_TITLES_FREE_TEXT',
    CORRESPONDENCE_ADDRESS = 'CORRESPONDENCE_ADDRESS',
    IDENTITY_DOCUMENT_FIELDS = 'IDENTITY_DOCUMENT_FIELDS',
    OBFUSCATE_CONTACT_PERSONAL_ID_CODE = 'OBFUSCATE_CONTACT_PERSONAL_ID_CODE',
    ADMINISTRATOR_FIELD = 'ADMINISTRATOR_FIELD',
    NEW_CONTACTS_DEFAULT_TO_CLIENT = 'NEW_CONTACTS_DEFAULT_TO_CLIENT',
    CONTACT_TYPE_CONVERSION = 'CONTACT_TYPE_CONVERSION',
    NON_VERIFIED_CLIENT_ICON = 'NON_VERIFIED_CLIENT_ICON',
    OPPONENT_CHIP = 'OPPONENT_CHIP',
    DEFAULT_CURRENCY = 'DEFAULT_CURRENCY',
    OBFUSCATE_EMPLOYEE_PERSONAL_ID_CODE = 'OBFUSCATE_EMPLOYEE_PERSONAL_ID_CODE',
}

/** A contact's kind: its party type and relationship type, the columns of the Client form's required fields. */
export enum ContactKind {
    PERSON_CLIENT = 'PERSON_CLIENT',
    PERSON_LEAD = 'PERSON_LEAD',
    PERSON_NON_CLIENT = 'PERSON_NON_CLIENT',
    COMPANY_CLIENT = 'COMPANY_CLIENT',
    COMPANY_LEAD = 'COMPANY_LEAD',
    COMPANY_NON_CLIENT = 'COMPANY_NON_CLIENT',
}

const CONTACT_KINDS: Record<PartyType, Partial<Record<RelationshipTypeCode, ContactKind>>> = {
    [PartyType.PERSON]: {
        [RelationshipTypeCode.NON_CLIENT]: ContactKind.PERSON_NON_CLIENT,
        [RelationshipTypeCode.LEAD]: ContactKind.PERSON_LEAD,
        [RelationshipTypeCode.CLIENT]: ContactKind.PERSON_CLIENT,
    },
    [PartyType.COMPANY]: {
        [RelationshipTypeCode.NON_CLIENT]: ContactKind.COMPANY_NON_CLIENT,
        [RelationshipTypeCode.LEAD]: ContactKind.COMPANY_LEAD,
        [RelationshipTypeCode.CLIENT]: ContactKind.COMPANY_CLIENT,
    },
};

/** The kind of a contact of these types; undefined while either is unknown. */
export function contactKind(partyType?: PartyType, relationshipType?: RelationshipTypeCode) {
    return partyType && relationshipType ? CONTACT_KINDS[partyType]?.[relationshipType] : undefined;
}

/** The forms with a Required fields card. */
export type RequiredFieldForm = 'CLIENT' | 'CASE' | 'TIMECARD';

/** `TENANT` is every colleague who saves the record; `SELECTED` the picked ones only. */
export type RequiredFieldScope = 'TENANT' | 'SELECTED';

/** Who a required field is mandatory for. The picks are kept while the scope is `TENANT`. */
export interface RequiredFieldAudience {
    scope: RequiredFieldScope;
    jobRoles: { id: number; name: string }[];
    systemRoles: { id: number; name: string }[];
    employees: { id: number; fullName: string }[];
}

/**
 * One cell of the Client form's grid or one row of the Case or Timecard form, as
 * `GET /v1/back-office-settings/required-fields` lists them in page order. The VAT number row's tick is the entry of
 * field `VAT_OPTIONAL_IN_OWN_COUNTRY`, its `mandatory` the tick.
 */
export interface RequiredFieldElement {
    form: RequiredFieldForm;
    field: string;
    /** The contact kind of a Client form cell; null on the Case and Timecard forms and on the VAT tick. */
    kind: ContactKind | null;
    /** The setting the form shows the field under; a cell of a setting off for the viewer is not listed. */
    setting: Setting | null;
    mandatory: boolean;
    audience: RequiredFieldAudience;
}

/** The forms with a row on the "Listed in employee pickers" card. */
export type EmployeePickerForm = 'CONTACT' | 'CASE' | 'GROUP' | 'EMPLOYEE' | 'DELEGATION';

/**
 * A row of the "Listed in employee pickers" card, `FORM:FIELD`: the `listedIn` its employee picker passes to
 * `/v1/employees/min`, so the list holds only who the row lists.
 */
export enum ListedIn {
    CONTACT_RELATIONSHIP_PARTNER = 'CONTACT:RELATIONSHIP_PARTNER',
    CONTACT_RELATIONSHIP_MANAGER = 'CONTACT:RELATIONSHIP_MANAGER',
    CONTACT_ASSISTANT = 'CONTACT:ASSISTANT',
    CASE_SUPERVISED_BY = 'CASE:SUPERVISED_BY',
    CASE_ASSISTANT = 'CASE:ASSISTANT',
    GROUP_RELATIONSHIP_PARTNER = 'GROUP:RELATIONSHIP_PARTNER',
    GROUP_RELATIONSHIP_MANAGER = 'GROUP:RELATIONSHIP_MANAGER',
    EMPLOYEE_SUPERVISOR = 'EMPLOYEE:SUPERVISOR',
    DELEGATION_SUBSTITUTE_SUPERVISING_PARTNER = 'DELEGATION:SUBSTITUTE_SUPERVISING_PARTNER',
}

/** Who an employee picker lists. The picks are kept while the scope is `TENANT`. */
export interface EmployeePickerAudience {
    scope: RequiredFieldScope;
    jobRoles: { id: number; name: string }[];
    employees: { id: number; fullName: string }[];
}

/** A row of the card, as `GET /v1/back-office-settings/employee-pickers` lists them in page order. */
export interface EmployeePickerElement {
    form: EmployeePickerForm;
    field: string;
    /** The setting the form shows the field under; a row of a setting off for the viewer is not listed. */
    setting: Setting | null;
    audience: EmployeePickerAudience;
}
